import i18n from '@/utils/i18n';
import { DEFAULT_BUTTON_TEXT_FIELDS } from '@/contexts/dynamic-config-utils';
import { formatCurrency } from '@/utils/money';
import { getItemWeightText, getPalletCountText } from '@/utils/order-summary';
import { OrderSummarySummationOptions } from '@/contexts/dynamic-config';
import { QuantityMapType } from '@/screens/new-order/hooks/use-quantity-reducer';
import { useCallback, useMemo } from 'react';
import { useDynamicConfigValue } from './use-dynamic-config-value';
import {
  useItemDisplayOptions,
  useNewOrderContext,
} from '@/contexts/new-order-context';
import {
  useNewOrderQuantityStore,
  useNewOrderQuantityStoreApi,
} from '@/stores/new-order-quantity-store';
import { useShallow } from 'zustand/shallow';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';

// Subscribe to just the visible quantities and not the entire quantity state
const useNonZeroQuantityMap = () => {
  return useNewOrderQuantityStore(
    useShallow((s) => {
      const quantityMap = {
        ...s.quantityState.savedQuantityMap,
        ...s.quantityState.localQuantityMap,
      };
      return Object.keys(quantityMap).reduce<QuantityMapType>(
        (acc, variantPackUUID) => {
          const quantity = quantityMap[variantPackUUID];
          if (quantity) {
            acc[variantPackUUID] = quantity;
          }
          return acc;
        },
        {},
      );
    }),
  );
};

const useHasOrderQuantities = () =>
  useNewOrderQuantityStore((s) => {
    const { localQuantityMap, savedQuantityMap } = s.quantityState;
    return Object.values({ ...savedQuantityMap, ...localQuantityMap }).some(
      (quantity) => Boolean(quantity),
    );
  });

const useGetOrderPriceText = () => {
  const { currencyCode, getVariantPackInfo } = useVariantPackInfoContext();
  const { displayItemPrices } = useItemDisplayOptions();

  return useCallback(
    (quantityMap: QuantityMapType) => {
      const variantPackUUIDs = Object.keys(quantityMap);

      let priceInMicros: number | null = 0;

      for (let variantPackUUID of variantPackUUIDs) {
        const itemQuantity = quantityMap[variantPackUUID] || 0;
        if (itemQuantity > 0) {
          const variantPackInfo = getVariantPackInfo(variantPackUUID);
          const itemPriceInMicros =
            typeof variantPackInfo?.priceInMicros === 'number'
              ? variantPackInfo.priceInMicros * variantPackInfo.unitCount
              : undefined;
          if (displayItemPrices && typeof itemPriceInMicros === 'number') {
            priceInMicros += itemPriceInMicros * itemQuantity;
          } else {
            // Set to null if there is any item without a price and there are no promotions
            priceInMicros = null;
            break;
          }
        }
      }

      return typeof priceInMicros === 'number'
        ? formatCurrency(priceInMicros, currencyCode)
        : null;
    },
    [currencyCode, displayItemPrices, getVariantPackInfo],
  );
};

const useGetOrderItemCountText = () => {
  return useCallback((quantityMap: QuantityMapType) => {
    const variantPackUUIDs = Object.keys(quantityMap);
    return i18n.t(
      variantPackUUIDs.length === 1 ? 'item_count' : 'items_count',
      {
        count: variantPackUUIDs.length,
      },
    );
  }, []);
};

const useGetOrderUnitCountText = () => {
  return useCallback((quantityMap: QuantityMapType) => {
    const totalUnitsInOrder = Object.keys(quantityMap).reduce(
      (acc, variantPackUUID) => acc + (quantityMap[variantPackUUID] || 0),
      0,
    );
    return i18n.t(
      totalUnitsInOrder === 1 ? 'unit_count' : 'unit_count_plural',
      {
        count: totalUnitsInOrder,
      },
    );
  }, []);
};

const useGetOrderPalletCountText = () => {
  const quantityStore = useNewOrderQuantityStoreApi();
  return useCallback(
    (quantityMap: QuantityMapType) => {
      return getPalletCountText(
        quantityMap,
        quantityStore.getState().quantityState.variantPackDetailMap,
      );
    },
    [quantityStore],
  );
};

const useGetOrderItemWeightText = () => {
  const quantityStore = useNewOrderQuantityStoreApi();
  return useCallback(
    (quantityMap: QuantityMapType) => {
      return getItemWeightText(
        quantityMap,
        quantityStore.getState().quantityState.variantPackDetailMap,
      );
    },
    [quantityStore],
  );
};

type GetTextFunctionMap = {
  [key in OrderSummarySummationOptions]: (
    quantityMap: QuantityMapType,
  ) => string | null;
};

export const useOrderButtonText = () => {
  const orderSummaryConfig = useDynamicConfigValue('order_summary_config');
  const buttonTextFields =
    orderSummaryConfig?.button_text_fields || DEFAULT_BUTTON_TEXT_FIELDS;
  const getItemCountText = useGetOrderItemCountText();
  const getPriceText = useGetOrderPriceText();
  const getUnitCountText = useGetOrderUnitCountText();
  const getPalletSizeText = useGetOrderPalletCountText();
  const getWeightText = useGetOrderItemWeightText();
  const quantityMap = useNonZeroQuantityMap();

  return useMemo(() => {
    const variantPackUUIDs = Object.keys(quantityMap);

    if (!variantPackUUIDs.length) {
      return null;
    }
    const optionToTextMap: GetTextFunctionMap = {
      [OrderSummarySummationOptions.ITEM_TYPE]: () => null,
      [OrderSummarySummationOptions.ITEM_WEIGHTS]: getWeightText,
      [OrderSummarySummationOptions.ORDER_ITEMS]: getItemCountText,
      [OrderSummarySummationOptions.PALLET_COUNT]: getPalletSizeText,
      [OrderSummarySummationOptions.SUBTOTAL]: getPriceText,
      [OrderSummarySummationOptions.UNITS]: getUnitCountText,
    };
    const textFields = buttonTextFields.reduce<string[]>((acc, field) => {
      const text = optionToTextMap[field]?.(quantityMap);
      return text ? [...acc, text] : acc;
    }, []);

    return textFields.length === 0
      ? getItemCountText(quantityMap)
      : textFields.join(' · ');
  }, [
    quantityMap,
    getWeightText,
    getItemCountText,
    getPalletSizeText,
    getPriceText,
    getUnitCountText,
    buttonTextFields,
  ]);
};

export const useShowCartButton = () => {
  const hasQuantities = useHasOrderQuantities();
  const { multiCartEnabled } = useNewOrderContext();

  return hasQuantities || multiCartEnabled;
};

export const useActiveCartName = () => {
  const { draftOrder, hasResponse, multiCartEnabled } = useNewOrderContext();

  if (!multiCartEnabled || !hasResponse) {
    return null;
  }

  return (
    draftOrder?.storefront_metadata?.name || i18n.t('multi_cart.default_cart')
  );
};
